import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { get, applyVerticalStyles } from 'ctm-util-functions';
import { quoteFormSelector } from 'ctm-selectors';
import { radioButtonGroupUIComponent } from 'ctm-css-themes';
import { FormattedInput } from 'ctm-components';
import { getBrand } from 'ctm-app/client/store/selectors';

import { SimpleFlexbox } from '../../flexbox/flexbox';
import { setFormValue, elementTouched } from '../actions';
import { getRuleRangeValues } from '../utils';

import styles from './formatted_input_with_buttons.css';

/**
 * FormattedInput component with increment/decrement buttons
 * Allows users to adjust numeric values using +/- buttons
 */
class _FormattedInputWithButtons extends Component {

    constructor(props) {
        super(props);
        this.state = { inputKey: 0, pendingRemount: false };
        this.styles = applyVerticalStyles(radioButtonGroupUIComponent, 'radio_button');
        this.numberMask = createNumberMask({
            prefix: '$',
            allowLeadingZeroes: true,
            allowDecimal: false,
            includeThousandsSeparator: true
        });
    }

    /**
     * Remounts FormattedInput once the new value from a button interaction has
     * propagated through Redux and arrived as a prop, ensuring customMask is applied.
     * Using componentDidUpdate (rather than incrementing inputKey directly in adjustValue)
     * prevents the remount from firing before the updated value prop is available.
     * @param {Object} prevProps - The previous props before the update
     */
    componentDidUpdate(prevProps) {
        const { pendingRemount } = this.state;
        const { value } = this.props;
        if (pendingRemount && prevProps.value !== value) {
            this.setState((prev) => ({ inputKey: prev.inputKey + 1, pendingRemount: false }));
        }
    }

    /**
     * Adjusts the value by the specified amount with rounding
     * @param {Number} adjustment - The amount to adjust (positive or negative)
     */
    adjustValue = (adjustment) => {
        const { schema, value, dispatch, selectedValues, rules, formData } = this.props;
        const { amount = 1, min, max } = get(schema, 'field.buttonConfig', {});
        const placeholderKey = get(schema, 'field.placeholderKey');
        const placeholderValue = placeholderKey ? get(formData, placeholderKey) : undefined;

        // Get min/max from validation rule if they're placeholders
        let { resolvedMin, resolvedMax } = _FormattedInputWithButtons.getInputRangeFromValidationRule(rules, min, max, selectedValues);

        // Convert to numbers
        resolvedMin = parseInt(resolvedMin, 10);
        resolvedMax = parseInt(resolvedMax, 10);

        // Parse current value, removing $ symbol, commas and handling empty values
        const cleanValue = (value || '0').toString().replace(/[$,]/g, '');
        const currentValue = parseInt(cleanValue, 10) || 0;

        let newValue;

        if (currentValue === 0 && placeholderValue) {
            newValue = placeholderValue;
        } else if (!isNaN(resolvedMin) && currentValue < resolvedMin && adjustment > 0) {
            newValue = resolvedMin;
        } else if (!isNaN(resolvedMax) && currentValue > resolvedMax && adjustment < 0) {
            newValue = resolvedMax;
        } else if (!isNaN(resolvedMin) && currentValue <= resolvedMin && adjustment < 0) {
            newValue = resolvedMin;
        } else if (!isNaN(resolvedMax) && currentValue >= resolvedMax && adjustment > 0) {
            newValue = resolvedMax;
        } else {
            const isIncrement = adjustment > 0;
            const absAmount = Math.abs(amount);

            // Strict directional stepping: increment => next boundary, decrement => previous boundary.
            if (isIncrement) {
                newValue = (Math.floor(currentValue / absAmount) + 1) * absAmount;
            } else {
                newValue = (Math.ceil(currentValue / absAmount) - 1) * absAmount;
            }

            // Apply min/max constraints
            if (!isNaN(resolvedMin) && newValue < resolvedMin) {
                newValue = resolvedMin;
            }
            if (!isNaN(resolvedMax) && newValue > resolvedMax) {
                newValue = resolvedMax;
            }
        }

        // Use backendIdentifier like Input component does
        const identifier = schema.backendIdentifier || schema.identifier;

        // Dispatch the new value as a string
        dispatch(
            setFormValue(
                identifier,
                newValue.toString(),
                schema.stateKey,
                true
            )
        );

        // Increment inputKey to remount FormattedInput, ensuring the customMask re-applies
        this.setState({ pendingRemount: true });

        // Trigger touch to enable validation
        dispatch(
            elementTouched(
                schema.touchIdentifier || identifier,
                schema.stateKey
            )
        );
    };

    handleDecrement = () => {
        const { schema } = this.props;
        const { amount = 10000 } = get(schema, 'field.buttonConfig', {});
        this.adjustValue(-amount);
    };

    handleIncrement = () => {
        const { schema } = this.props;
        const { amount = 10000 } = get(schema, 'field.buttonConfig', {});
        this.adjustValue(amount);
    };

    resetAmount = () => {
        const { schema, dispatch, formData } = this.props;
        const identifier = schema.backendIdentifier || schema.identifier;
        const resetAmountKey = get(schema, 'field.resetAmountIdentifier');
        const resetAmountIdentifier = resetAmountKey ? get(formData, resetAmountKey) : undefined;

        if (resetAmountIdentifier) {
            dispatch(
                setFormValue(
                    identifier,
                    resetAmountIdentifier.toString(),
                    schema.stateKey,
                    true
                )
            );
            this.setState({ pendingRemount: true });
        }
    };

    static getInputRangeFromValidationRule(rules, min, max, selectedValues) {
        let rangeValues = {};
        if (rules && rules.validation) {
            rangeValues = getRuleRangeValues(rules.validation);
        }

        // Resolve min/max from selectedValues if they are placeholders
        let resolvedMin = min;
        let resolvedMax = max;

        if (typeof min === 'string' && min.includes('{')) {
            const key = min.replace(/[{}]/g, '');
            resolvedMin = rangeValues[key] || get(selectedValues, key);
        }
        if (typeof max === 'string' && max.includes('{')) {
            const key = max.replace(/[{}]/g, '');
            resolvedMax = rangeValues[key] || get(selectedValues, key);
        }
        return { resolvedMin, resolvedMax };
    }

    render() {
        const { schema, value, selectedValues, rules, hasError, brandCode, formData, ...restProps } = this.props;
        const { inputKey } = this.state;
        const { buttonConfig = {} } = get(schema, 'field', {});
        const { min, max, dataIdPrefix } = buttonConfig;

        const { placeholderKey = {} } = get(schema, 'field', {});
        const placeholderValue = get(formData, placeholderKey);

        // Get min/max from validation rule if they're placeholders
        const { resolvedMin, resolvedMax } = _FormattedInputWithButtons.getInputRangeFromValidationRule(rules, min, max, selectedValues);
        const minMaxLabel = (resolvedMin !== undefined && resolvedMax !== undefined) ? `Min: $${resolvedMin.toLocaleString()} - Max: $${resolvedMax.toLocaleString()}` : '';

        // Parse current value for button state, removing $ and commas
        const currentValue = parseInt((value || '0').replace(/[$,]/g, ''), 10) || 0;
        const isDecrementDisabled = resolvedMin === undefined;
        const isIncrementDisabled = resolvedMax === undefined;

        // Button UI Component class
        const UIComponent = 'radio_button_group_small_rounded';
        const buttonClasses = classNames(
            this.styles[UIComponent],
            styles.formattedInputButton,
            brandCode && styles[brandCode.toLowerCase()]
        );

        // Create modified schema with custom mask configuration to include $ prefix
        const modifiedSchema = {
            ...schema,
            twoWayBinding: true,
            field: {
                ...schema.field,
                customMask: this.numberMask
            }
        };

        // Normalize input and medium values for comparison
        const normalizedInputValue = typeof value === 'string' ? value.replace(/[$,]/g, '') : value;
        let midValue = '';
        const resetAmountKey = get(schema, 'field.resetAmountIdentifier');
        const resetAmountIdentifier = resetAmountKey ? get(formData, resetAmountKey) : undefined;
        if (resetAmountIdentifier) {
            midValue = get(formData, resetAmountKey).toString();
        }
        const normalizedMidValue = midValue.replace(/[$,]/g, '');
        // Also hide Reset when the "calculated" sublabel is not showing — i.e. the API response
        // was unavailable or outside thresholds. In those states there is no valid suggested
        // amount to reset to, so the button would be misleading.
        const isCalculatedSubLabelVisible = get(rules, 'isRebuildCostCalculated.result');
        const isResetDisabled = normalizedInputValue === normalizedMidValue 
        || resetAmountIdentifier === undefined 
        || isCalculatedSubLabelVisible === false;

        let formattedPlaceholder = '';
        
        if (placeholderValue) {
            const placeholderValueMasked = `$${Number(placeholderValue).toLocaleString('en-AU')}`;
            formattedPlaceholder = `E.g. ${placeholderValueMasked}`;
        }

        // this forces the input to show placeholder text instead of 0 when value is 0 or '0'
        const effectiveValue = (value === '0' || value === 0) ? '' : value;

        return (
            <SimpleFlexbox className={styles.formattedInputWithButtonsWrapper}>
                <SimpleFlexbox
                    display="flex"
                    alignItems="center"
                    className={classNames(
                        styles.formattedInputWithButtons,
                        brandCode && brandCode.toLowerCase()
                    )}
                >
                    <button
                        type="button"
                        className={classNames(
                            buttonClasses,
                            styles.decrementButton,
                            brandCode && brandCode.toLowerCase(),
                            { [styles.buttonError]: hasError }
                        )}
                        onClick={this.handleDecrement}
                        disabled={isDecrementDisabled}
                        data-id={`decrement-${dataIdPrefix}`}
                    >
                        -
                    </button>
                    <div className={classNames(styles.formattedInputWrapper, brandCode && styles[brandCode.toLowerCase()], { [styles.inputError]: hasError })}>
                        <FormattedInput
                            {...restProps}
                            key={inputKey}
                            schema={modifiedSchema}
                            value={effectiveValue}
                            placeholder={formattedPlaceholder}
                        />
                    </div>
                    <button
                        type="button"
                        className={classNames(
                            buttonClasses,
                            styles.incrementButton,
                            brandCode && brandCode.toLowerCase(),
                            { [styles.buttonError]: hasError }
                        )}
                        onClick={this.handleIncrement}
                        disabled={isIncrementDisabled}
                        data-id={`increment-${dataIdPrefix}`}
                    >
                        +
                    </button>
                </SimpleFlexbox>
                <SimpleFlexbox className={styles.rangeAndResetContainer}>
                    <span>{minMaxLabel}</span>
                    {!isResetDisabled && (
                        <button
                            type="button"
                            className={classNames(
                                styles.formattedResetButton,
                                brandCode && styles[brandCode.toLowerCase()],
                                styles.resetAmount)}
                            onClick={this.resetAmount}
                        >
                            Reset Amount
                        </button>
                    )}
                </SimpleFlexbox>
            </SimpleFlexbox>
        );
    }

}

_FormattedInputWithButtons.propTypes = {
    schema: PropTypes.object.isRequired,
    value: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    selectedValues: PropTypes.object,
    rules: PropTypes.object,
    formData: PropTypes.object,
    hasError: PropTypes.bool,
    brandCode: PropTypes.string
};

/**
 * Map entire form data from state to props
 * @param {Immutable.Map} state - The application state
 * @param {Object} ownProps - The component's own props
 * @returns {Object} - The form data and error state to be added to props
 */
const mapStateToProps = (state, ownProps) => {
    const form = quoteFormSelector(state);
    // Convert Immutable Map to plain JavaScript object for API payload
    const formData = form ? form.toJS() : {};

    // Read touched state from Redux, matching the field_row pattern
    const id = ownProps.schema.identifier;
    const stateKey = ownProps.schema.stateKey;
    const touchId = ownProps.schema.touchIdentifier || id;
    const touched = state.getIn([...stateKey.split('.'), 'validation', 'touches', touchId]);
    // Check for validation errors after the user has interacted with the field
    const hasError = !!(touched && ownProps.rules && ownProps.rules.validation && ownProps.rules.validation.result === false);

    const brandCode = getBrand(state);
    return {
        formData,
        hasError,
        brandCode
    };
};

const FormattedInputWithButtons = connect(mapStateToProps)(_FormattedInputWithButtons);

export { _FormattedInputWithButtons, FormattedInputWithButtons };
